/**
 * WriteReview.jsx
 *
 * Global slide-in "Write a Review" tab — mounts once in your app root.
 *
 * Duplicate detection (two layers):
 *  1. localStorage  — instant, survives page refresh, checked before the panel opens
 *  2. Server 409    — catches users who cleared storage or switched browsers
 *
 * localStorage keys:
 *  "wr_general"              → submitted a general review
 *  "wr_service_{id}"         → submitted a review for service id
 *  "wr_product_{id}"         → submitted a review for product id
 *
 * Photo upload:
 *  Optional face photo (JPG / PNG / WEBP, max 5 MB).
 *  Previewed inline before submission; sent as multipart/form-data.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import config from "../config";

const { API_BASE } = config;

const fetchJSON = (url) =>
  fetch(url).then((r) => {
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json();
  });

const STAR_LABELS = ["", "Poor", "Fair", "Good", "Very Good", "Excellent"];

const ALLOWED_PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_PHOTO_BYTES      = 5 * 1024 * 1024; // 5 MB

/* ── localStorage helpers ── */
const LS_PREFIX = "wr_";

function lsKey(type, id) {
  if (!type) return `${LS_PREFIX}general`;
  return `${LS_PREFIX}${type}_${id || "general"}`;
}

function hasReviewed(type, id) {
  try {
    return !!localStorage.getItem(lsKey(type, id));
  } catch {
    return false;
  }
}

function markReviewed(type, id) {
  try {
    localStorage.setItem(lsKey(type, id), "1");
  } catch { /* silent */ }
}

/* ── Star picker ── */
function StarPicker({ value, onChange }) {
  const [hovered, setHovered] = useState(0);
  const display = hovered || value;

  return (
    <div className="wr-stars" role="radiogroup" aria-label="Rating">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          className={`wr-star ${display >= n ? "wr-star--on" : ""}`}
          onMouseEnter={() => setHovered(n)}
          onMouseLeave={() => setHovered(0)}
          onClick={() => onChange(n)}
          aria-label={`${n} star${n > 1 ? "s" : ""}`}
          aria-pressed={value === n}
        >
          <svg viewBox="0 0 24 24" width="28" height="28">
            <path
              d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
              fill={display >= n ? "#f59e0b" : "none"}
              stroke={display >= n ? "#f59e0b" : "#cbd5e1"}
              strokeWidth="1.5"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      ))}
      {display > 0 && (
        <span className="wr-star-label">{STAR_LABELS[display]}</span>
      )}
    </div>
  );
}

/* ── Photo picker ── */
function PhotoPicker({ file, preview, onChange, onRemove, error }) {
  const inputRef = useRef(null);

  return (
    <div className="wr-photo-picker">
      {preview ? (
        <div className="wr-photo-preview">
          <img src={preview} alt="Your photo preview" className="wr-photo-img" />
          <button
            type="button"
            className="wr-photo-remove"
            onClick={onRemove}
            aria-label="Remove photo"
          >
            <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
              <path d="M4 4l12 12M16 4L4 16" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
            </svg>
          </button>
          <span className="wr-photo-name">{file?.name}</span>
        </div>
      ) : (
        <button
          type="button"
          className="wr-photo-trigger"
          onClick={() => inputRef.current?.click()}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="5" width="18" height="14" rx="2"
              stroke="currentColor" strokeWidth="1.6" />
            <circle cx="12" cy="12" r="3.5"
              stroke="currentColor" strokeWidth="1.6" />
            <path d="M9 5l1.5-2h3L15 5" stroke="currentColor" strokeWidth="1.6"
              strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span>Add your face photo <span className="wr-photo-req">(required)</span></span>
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        style={{ display: "none" }}
        onChange={onChange}
      />

      {error && <span className="wr-err">{error}</span>}
      <span className="wr-photo-hint">JPG, PNG or WEBP · max 5 MB · square photos look best</span>
    </div>
  );
}

/* ── Already-reviewed panel state ── */
function AlreadyReviewed({ onClose }) {
  return (
    <div className="wr-already">
      <div className="wr-already__icon">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" fill="#eff6ff" stroke="#3b82f6" strokeWidth="1.5"/>
          <path d="M12 7v5l3 3" stroke="#3b82f6" strokeWidth="1.8" strokeLinecap="round"/>
        </svg>
      </div>
      <h3 className="wr-already__heading">Review already submitted</h3>
      <p className="wr-already__body">
        We've already received a review from you for this item. We appreciate your feedback!
      </p>
      <p className="wr-already__sub">
        If you believe this is a mistake, please contact us directly.
      </p>
      <button className="wr-already__btn" onClick={onClose}>Got it</button>
    </div>
  );
}

/* ── Success modal ── */
function SuccessModal({ onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 6000);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
    <div className="wr-modal-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="wr-modal" onClick={(e) => e.stopPropagation()}>
        <div className="wr-modal__icon">
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="11" fill="#ecfdf5" stroke="#10b981" strokeWidth="1.5"/>
            <path d="M7 12.5l3.5 3.5 6.5-7" stroke="#10b981" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <h2 className="wr-modal__heading">Thank you for your review!</h2>
        <p className="wr-modal__body">
          Your feedback has been submitted and is currently under review. Once our team
          approves it, it will appear on our website.
        </p>
        <p className="wr-modal__sub">We appreciate you taking the time to share your experience.</p>
        <button className="wr-modal__btn" onClick={onClose}>Done</button>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════ */
export default function WriteReview() {
  const [open, setOpen]             = useState(false);
  const [products, setProducts]     = useState([]);
  const [services, setServices]     = useState([]);
  const [submitted, setSubmitted]   = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [alreadyReviewed, setAlreadyReviewed] = useState(false);
  const [errors, setErrors]         = useState({});

  /* Photo state */
  const [photoFile, setPhotoFile]       = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  const panelRef = useRef(null);

  const emptyForm = {
    name: "", company: "", review_for_type: "", review_for_id: "",
    rating: 0, position: "", content: "",
    testimonial_date: new Date().toISOString().slice(0, 10),
  };
  const [form, setForm] = useState(emptyForm);

  /* ── Load products + services once, both normalised to { id, title } ── */
  useEffect(() => {
    fetchJSON(`${API_BASE}/products.php`)
      .then((d) => {
        if (!Array.isArray(d)) return;
        setProducts(
          d
            .filter((p) => p.active != 0)
            .map((p) => ({
              id:    p.id,
              title: p.title ?? p.name ?? `Product ${p.id}`,
            }))
        );
      })
      .catch(() => {});

    fetchJSON(`${API_BASE}/services.php`)
      .then((d) => {
        if (!Array.isArray(d)) return;
        setServices(
          d
            .filter((s) => s.active != 0)
            .map((s) => ({
              id:    s.id,
              title: s.title ?? s.name ?? `Service ${s.id}`,
            }))
        );
      })
      .catch(() => {});
  }, []);

  /* focus trap + Escape */
  useEffect(() => {
    if (!open) return;
    const focusable = panelRef.current?.querySelectorAll(
      'button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    focusable?.[0]?.focus();
    const handleKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open]);

  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  /* Check localStorage whenever type or id changes */
  useEffect(() => {
    if (!open) return;
    const already = hasReviewed(form.review_for_type, form.review_for_id);
    setAlreadyReviewed(already);
  }, [form.review_for_type, form.review_for_id, open]);

  /* Also check on open */
  const handleOpen = useCallback(() => {
    const already = hasReviewed(form.review_for_type, form.review_for_id);
    setAlreadyReviewed(already);
    setOpen((v) => !v);
  }, [form.review_for_type, form.review_for_id]);

  /* ── Photo handlers ── */
  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_PHOTO_TYPES.includes(file.type)) {
      setErrors((prev) => ({ ...prev, photo: "Only JPG, PNG or WEBP images are allowed." }));
      return;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      setErrors((prev) => ({ ...prev, photo: "Photo must be under 5 MB." }));
      return;
    }

    setErrors((prev) => { const next = { ...prev }; delete next.photo; return next; });
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));

    /* reset the input so the same file can be re-selected after removal */
    e.target.value = "";
  };

  const handlePhotoRemove = () => {
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoFile(null);
    setPhotoPreview(null);
    setErrors((prev) => { const next = { ...prev }; delete next.photo; return next; });
  };

  /* Clean up object URL on unmount */
  useEffect(() => {
    return () => { if (photoPreview) URL.revokeObjectURL(photoPreview); };
  }, [photoPreview]);

  /* ── Validation ── */
  const validate = () => {
    const e = {};
    if (!form.name.trim())    e.name    = "Your name is required.";
    if (!form.content.trim()) e.content = "Please write your review.";
    if (!form.rating)         e.rating  = "Please select a star rating.";
    if (!photoFile)           e.photo   = "Please add a face photo — it helps build trust!";
    if (form.review_for_type && !form.review_for_id)
      e.review_for_id = "Please select a specific item.";
    return e;
  };

  /* ── Submit ── */
  const handleSubmit = async (e) => {
    e.preventDefault();

    /* Client-side duplicate guard */
    if (hasReviewed(form.review_for_type, form.review_for_id)) {
      setAlreadyReviewed(true);
      return;
    }

    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setSubmitting(true);

    try {
      const showOn = form.review_for_type === "service" ? "services"
                   : form.review_for_type === "product" ? "products" : "";

      const body = new FormData();
      body.append("name",             form.name.trim());
      body.append("company",          form.company.trim());
      body.append("position",         form.position.trim());
      body.append("content",          form.content.trim());
      body.append("rating",           form.rating);
      body.append("testimonial_date", form.testimonial_date);
      body.append("show_on_pages",    showOn);
      body.append("review_for_type",  form.review_for_type);
      body.append("review_for_id",    form.review_for_id);

      /* Attach photo if one was chosen */
      if (photoFile) {
        body.append("photo_filename", photoFile, photoFile.name);
      }

      const res = await fetch(`${API_BASE}/submit-review.php`, {
        method: "POST",
        body,
      });

      const data = await res.json().catch(() => ({}));

      if (res.status === 409 || data?.error === "duplicate") {
        markReviewed(form.review_for_type, form.review_for_id);
        setAlreadyReviewed(true);
        return;
      }

      if (res.status === 429) {
        setErrors({ submit: data?.message || "Too many submissions. Please try again later." });
        return;
      }

      if (!res.ok) {
        setErrors({ submit: data?.message || "Something went wrong. Please try again." });
        return;
      }

      /* Success */
      markReviewed(form.review_for_type, form.review_for_id);
      handlePhotoRemove();
      setOpen(false);
      setSubmitted(true);
      setForm(emptyForm);

    } catch {
      setErrors({ submit: "Something went wrong. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  const itemOptions =
    form.review_for_type === "product" ? products :
    form.review_for_type === "service" ? services : [];

  return (
    <>
      {/* ── Pull / Close tab — single button, swaps label when open ── */}
      <button
        className={`wr-tab ${open ? "wr-tab--open" : ""}`}
        onClick={handleOpen}
        aria-expanded={open}
        aria-label={open ? "Close review panel" : "Write a review"}
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          {open ? (
            /* Panel is open → chevron points RIGHT to "push" it away */
            <path d="M5 3l4 4-4 4" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round" />
          ) : (
            /* Panel is closed → chevron points LEFT to "pull" it in */
            <path d="M9 3l-4 4 4 4" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round" />
          )}
        </svg>
        <span className="wr-tab__label">{open ? "Close" : "Write a Review"}</span>
      </button>

      {/* ── Overlay ── */}
      {open && (
        <div className="wr-overlay" onClick={() => setOpen(false)} aria-hidden="true" />
      )}

      {/* ── Slide-in panel ── */}
      <aside
        ref={panelRef}
        className={`wr-panel ${open ? "wr-panel--open" : ""}`}
        aria-label="Write a review"
        aria-hidden={!open}
      >
        {/* Panel header */}
        <div className="wr-panel__header">
          <div className="wr-panel__header-left">
            <div className="wr-panel__icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
                  stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div>
              <h2 className="wr-panel__title">Write a Review</h2>
              <p className="wr-panel__sub">Your feedback helps us improve</p>
            </div>
          </div>
          <button className="wr-panel__close" onClick={() => setOpen(false)} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
              <path d="M4 4l12 12M16 4L4 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* ── Already reviewed state ── */}
        {alreadyReviewed ? (
          <AlreadyReviewed onClose={() => setOpen(false)} />
        ) : (
          /* ── Normal form ── */
          <form className="wr-form" onSubmit={handleSubmit} noValidate>
            <div className="wr-form__body">

              {/* Rating */}
              <div className="wr-field">
                <label className="wr-label">Your Rating <span className="wr-req">*</span></label>
                <StarPicker value={form.rating} onChange={(v) => set("rating", v)} />
                {errors.rating && <span className="wr-err">{errors.rating}</span>}
              </div>

              {/* Reviewing for type */}
              <div className="wr-field">
                <label className="wr-label" htmlFor="wr-type">Reviewing for</label>
                <select
                  id="wr-type"
                  className="wr-select"
                  value={form.review_for_type}
                  onChange={(e) => {
                    set("review_for_type", e.target.value);
                    set("review_for_id", "");
                  }}
                >
                  <option value="">— General / Other —</option>
                  <option value="product">A Product</option>
                </select>
              </div>

              {/* Specific item dropdown */}
              {form.review_for_type && (
                <div className="wr-field">
                  <label className="wr-label" htmlFor="wr-item">
                    {form.review_for_type === "service" ? "Which service?" : "Which product?"}
                    <span className="wr-req"> *</span>
                  </label>
                  <select
                    id="wr-item"
                    className={`wr-select ${errors.review_for_id ? "wr-select--err" : ""}`}
                    value={form.review_for_id}
                    onChange={(e) => set("review_for_id", e.target.value)}
                  >
                    <option value="">— Select —</option>
                    {itemOptions.map((item) => (
                      <option key={item.id} value={item.id}>{item.title}</option>
                    ))}
                  </select>
                  {errors.review_for_id && <span className="wr-err">{errors.review_for_id}</span>}

                  {/* Inline duplicate warning */}
                  {form.review_for_id && hasReviewed(form.review_for_type, form.review_for_id) && (
                    <div className="wr-dup-inline">
                      <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                        <circle cx="10" cy="10" r="9" stroke="#3b82f6" strokeWidth="1.5"/>
                        <path d="M10 6v4m0 4h.01" stroke="#3b82f6" strokeWidth="1.8"
                          strokeLinecap="round"/>
                      </svg>
                      You've already reviewed this item.
                    </div>
                  )}
                </div>
              )}

              {/* Name */}
              <div className="wr-field">
                <label className="wr-label" htmlFor="wr-name">
                  Your Name <span className="wr-req">*</span>
                </label>
                <input
                  id="wr-name"
                  type="text"
                  className={`wr-input ${errors.name ? "wr-input--err" : ""}`}
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  placeholder="e.g. John Smith"
                  maxLength={120}
                />
                {errors.name && <span className="wr-err">{errors.name}</span>}
              </div>

              {/* Company */}
              <div className="wr-field">
                <label className="wr-label" htmlFor="wr-company">Company (optional)</label>
                <input
                  id="wr-company"
                  type="text"
                  className="wr-input"
                  value={form.company}
                  onChange={(e) => set("company", e.target.value)}
                  placeholder="e.g. ABC Holdings"
                  maxLength={120}
                />
              </div>

              {/* Review title */}
              <div className="wr-field">
                <label className="wr-label" htmlFor="wr-position">Review Title (optional)</label>
                <input
                  id="wr-position"
                  type="text"
                  className="wr-input"
                  value={form.position}
                  onChange={(e) => set("position", e.target.value)}
                  placeholder="e.g. Excellent service and support"
                  maxLength={200}
                />
              </div>

              {/* Photo upload */}
              <div className="wr-field">
                <label className="wr-label">Your Photo <span className="wr-req">*</span></label>
                <PhotoPicker
                  file={photoFile}
                  preview={photoPreview}
                  onChange={handlePhotoChange}
                  onRemove={handlePhotoRemove}
                  error={errors.photo}
                />
              </div>

              {/* Content */}
              <div className="wr-field">
                <label className="wr-label" htmlFor="wr-content">
                  Your Review <span className="wr-req">*</span>
                </label>
                <textarea
                  id="wr-content"
                  className={`wr-textarea ${errors.content ? "wr-input--err" : ""}`}
                  value={form.content}
                  onChange={(e) => set("content", e.target.value)}
                  placeholder="Tell us about your experience…"
                  rows={5}
                  maxLength={2000}
                />
                <span className="wr-char">{form.content.length}/2000</span>
                {errors.content && <span className="wr-err">{errors.content}</span>}
              </div>

              {errors.submit && (
                <div className="wr-submit-err">{errors.submit}</div>
              )}
            </div>

            <div className="wr-form__footer">
              <p className="wr-disclaimer">Reviews are moderated before being published.</p>
              <button type="submit" className="wr-submit" disabled={submitting}>
                {submitting ? (
                  <><span className="wr-spinner" aria-hidden="true" /> Submitting…</>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M22 2L11 13M22 2L15 22l-4-9-9-4 20-7z"
                        stroke="currentColor" strokeWidth="2"
                        strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Submit Review
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </aside>

      {/* ── Success modal ── */}
      {submitted && <SuccessModal onClose={() => setSubmitted(false)} />}

      {/* ── Styles ── */}
      <style>{`
        /* Pull tab */
        .wr-tab {
          position: fixed; right: 0; top: 50%; transform: translateY(-50%);
          z-index: 600; display: flex; flex-direction: column;
          align-items: center; gap: 7px;
          background: #029add; color: #fff; border: none;
          border-radius: 8px 0 0 8px; padding: 16px 10px;
          cursor: pointer; box-shadow: -3px 0 14px rgba(0,0,0,.18);
          writing-mode: vertical-rl; text-orientation: mixed;
          transition: background .2s, right .35s cubic-bezier(.4,0,.2,1); font-family: 'Roboto', sans-serif;
        }
        .wr-tab:hover        { background: #013d97; }
        .wr-tab--open        { background: #013d97; right: 420px; }
        .wr-tab__label {
          font-size: 11px; font-weight: 800; letter-spacing: .08em;
          text-transform: uppercase; font-family: 'Roboto', sans-serif; line-height: 1;
        }

        /* Overlay */
        .wr-overlay {
          position: fixed; inset: 0; background: rgba(0,0,0,.35);
          z-index: 590; backdrop-filter: blur(2px);
        }

        /* Panel */
        .wr-panel {
          position: fixed; right: 0; top: 0; height: 100%;
          width: 420px; max-width: 100vw; z-index: 600;
          background: #f5f7fa;
          box-shadow: -6px 0 40px rgba(0,0,0,.18);
          display: flex; flex-direction: column;
          transform: translateX(100%);
          transition: transform .35s cubic-bezier(.4,0,.2,1);
          overflow: hidden;
        }
        .wr-panel--open { transform: translateX(0); }

        /* Panel header */
        .wr-panel__header {
          display: flex; align-items: center; justify-content: space-between;
          padding: 20px 20px 18px;
          background: linear-gradient(135deg, #013d97 0%, #029add 100%);
          flex-shrink: 0;
        }
        .wr-panel__header-left { display: flex; align-items: center; gap: 12px; }
        .wr-panel__icon {
          width: 38px; height: 38px; border-radius: 8px;
          background: rgba(255,255,255,.15);
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .wr-panel__title {
          font-size: 17px; font-weight: 800; color: #fff;
          margin: 0 0 2px; font-family: 'Roboto', sans-serif;
        }
        .wr-panel__sub { font-size: 12px; color: rgba(255,255,255,.72); margin: 0; font-family: 'Roboto', sans-serif; }
        .wr-panel__close {
          width: 34px; height: 34px; border-radius: 50%;
          background: rgba(255,255,255,.15); border: none; color: #fff;
          cursor: pointer; display: flex; align-items: center; justify-content: center;
          transition: background .2s; flex-shrink: 0;
        }
        .wr-panel__close:hover { background: rgba(255,255,255,.28); }

        /* Already reviewed */
        .wr-already {
          flex: 1; display: flex; flex-direction: column;
          align-items: center; justify-content: center;
          padding: 40px 28px; text-align: center; gap: 16px;
          font-family: 'Roboto', sans-serif;
        }
        .wr-already__icon {
          width: 70px; height: 70px; border-radius: 50%;
          background: #eff6ff; border: 3px solid #bfdbfe;
          display: flex; align-items: center; justify-content: center;
        }
        .wr-already__heading { font-size: 18px; font-weight: 800; color: #1a1a2e; margin: 0; }
        .wr-already__body { font-size: 14px; color: #5a5f72; line-height: 1.7; margin: 0; }
        .wr-already__sub  { font-size: 12px; color: #8890a0; margin: 0; }
        .wr-already__btn {
          margin-top: 8px; padding: 11px 32px;
          background: linear-gradient(135deg, #013d97, #029add);
          color: #fff; border: none; border-radius: 8px;
          font-size: 14px; font-weight: 800; cursor: pointer;
          font-family: 'Roboto', sans-serif; transition: opacity .2s;
        }
        .wr-already__btn:hover { opacity: .9; }

        /* Inline duplicate warning */
        .wr-dup-inline {
          display: flex; align-items: center; gap: 6px;
          margin-top: 6px; padding: 8px 12px;
          background: #eff6ff; border: 1px solid #bfdbfe;
          border-radius: 6px; font-size: 12.5px;
          color: #1d4ed8; font-family: 'Roboto', sans-serif;
        }

        /* Form */
        .wr-form { display: flex; flex-direction: column; flex: 1; overflow: hidden; }
        .wr-form__body {
          flex: 1; overflow-y: auto; padding: 22px 20px 8px;
          display: flex; flex-direction: column; gap: 16px;
        }
        .wr-form__body::-webkit-scrollbar { width: 5px; }
        .wr-form__body::-webkit-scrollbar-thumb { background: #cdd5e0; border-radius: 4px; }
        .wr-form__footer {
          padding: 16px 20px 20px; border-top: 1px solid #e5e8ef;
          background: #fff; flex-shrink: 0;
        }

        /* Fields */
        .wr-field { display: flex; flex-direction: column; gap: 6px; }
        .wr-label { font-size: 12.5px; font-weight: 700; color: #1a1a2e; font-family: 'Roboto', sans-serif; }
        .wr-req { color: #029add; }
        .wr-input, .wr-select, .wr-textarea {
          width: 100%; padding: 9px 12px; border: 1.5px solid #e0e4ef;
          border-radius: 8px; font-size: 13.5px; font-family: 'Roboto', sans-serif;
          color: #1a1a2e; background: #fff;
          transition: border-color .2s, box-shadow .2s; outline: none;
        }
        .wr-input:focus, .wr-select:focus, .wr-textarea:focus {
          border-color: #029add; box-shadow: 0 0 0 3px rgba(2,154,221,.12);
        }
        .wr-input--err, .wr-select--err { border-color: #ef4444 !important; }
        .wr-textarea { resize: vertical; min-height: 100px; }
        .wr-char { font-size: 11px; color: #8890a0; text-align: right; margin-top: -2px; font-family: 'Roboto', sans-serif; }
        .wr-err  { font-size: 11.5px; color: #ef4444; font-family: 'Roboto', sans-serif; }
        .wr-submit-err {
          padding: 10px 14px; background: #fef2f2; border: 1px solid #fecaca;
          border-radius: 8px; font-size: 13px; color: #991b1b; font-family: 'Roboto', sans-serif;
        }

        /* Stars */
        .wr-stars { display: flex; align-items: center; gap: 4px; }
        .wr-star {
          background: none; border: none; cursor: pointer; padding: 2px;
          transition: transform .15s; display: flex; align-items: center; justify-content: center;
        }
        .wr-star:hover { transform: scale(1.15); }
        .wr-star-label { font-size: 12px; font-weight: 700; color: #f59e0b; margin-left: 6px; font-family: 'Roboto', sans-serif; }

        /* ── Photo picker ── */
        .wr-photo-picker {
          display: flex; flex-direction: column; gap: 5px;
        }
        .wr-photo-trigger {
          display: flex; align-items: center; gap: 10px;
          width: 100%; padding: 11px 14px;
          border: 1.5px dashed #c8d0e0; border-radius: 8px;
          background: #f8fafc; color: #5a5f72;
          font-size: 13px; font-family: 'Roboto', sans-serif;
          cursor: pointer; transition: border-color .2s, background .2s;
          text-align: left;
        }
        .wr-photo-trigger:hover {
          border-color: #029add; background: #f0f8ff; color: #013d97;
        }
        .wr-photo-opt { color: #8890a0; font-weight: 400; }
        .wr-photo-req { color: #ef4444; font-weight: 600; }
        .wr-photo-hint {
          font-size: 11px; color: #8890a0; font-family: 'Roboto', sans-serif;
        }
        .wr-photo-preview {
          display: flex; align-items: center; gap: 10px;
          padding: 8px 12px;
          background: #f0f8ff; border: 1.5px solid #bfdbfe;
          border-radius: 8px;
        }
        .wr-photo-img {
          width: 48px; height: 48px; border-radius: 50%;
          object-fit: cover; border: 2px solid #fff;
          box-shadow: 0 1px 4px rgba(0,0,0,.12); flex-shrink: 0;
        }
        .wr-photo-name {
          flex: 1; font-size: 12px; color: #1a1a2e;
          font-family: 'Roboto', sans-serif;
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .wr-photo-remove {
          width: 26px; height: 26px; border-radius: 50%;
          background: #fee2e2; border: none; color: #dc2626;
          cursor: pointer; display: flex; align-items: center; justify-content: center;
          flex-shrink: 0; transition: background .2s;
        }
        .wr-photo-remove:hover { background: #fecaca; }

        /* Submit */
        .wr-disclaimer { font-size: 11px; color: #8890a0; margin: 0 0 12px; font-family: 'Roboto', sans-serif; }
        .wr-submit {
          width: 100%; display: flex; align-items: center; justify-content: center;
          gap: 8px; padding: 12px;
          background: linear-gradient(135deg, #013d97, #029add);
          color: #fff; border: none; border-radius: 8px;
          font-size: 14px; font-weight: 800; font-family: 'Roboto', sans-serif;
          cursor: pointer; transition: opacity .2s, transform .1s; letter-spacing: .02em;
        }
        .wr-submit:hover:not(:disabled) { opacity: .9; }
        .wr-submit:active:not(:disabled) { transform: scale(.98); }
        .wr-submit:disabled { opacity: .65; cursor: not-allowed; }
        .wr-spinner {
          width: 16px; height: 16px; border: 2px solid rgba(255,255,255,.35);
          border-top-color: #fff; border-radius: 50%;
          animation: wr-spin .7s linear infinite; flex-shrink: 0;
        }
        @keyframes wr-spin { to { transform: rotate(360deg); } }

        /* Success modal */
        .wr-modal-backdrop {
          position: fixed; inset: 0; z-index: 700;
          background: rgba(5,10,25,.6);
          display: flex; align-items: center; justify-content: center; padding: 24px;
          backdrop-filter: blur(3px); animation: wr-fade-in .25s ease;
        }
        @keyframes wr-fade-in { from { opacity: 0; } to { opacity: 1; } }
        .wr-modal {
          background: #fff; border-radius: 16px; padding: 40px 36px 36px;
          max-width: 420px; width: 100%; text-align: center;
          box-shadow: 0 24px 60px rgba(0,0,0,.2);
          animation: wr-slide-up .3s cubic-bezier(.4,0,.2,1);
          font-family: 'Roboto', sans-serif;
        }
        @keyframes wr-slide-up {
          from { transform: translateY(24px); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        .wr-modal__icon {
          width: 72px; height: 72px; border-radius: 50%; background: #ecfdf5;
          display: flex; align-items: center; justify-content: center;
          margin: 0 auto 20px; border: 3px solid #a7f3d0;
        }
        .wr-modal__heading { font-size: 22px; font-weight: 800; color: #1a1a2e; margin: 0 0 12px; line-height: 1.2; }
        .wr-modal__body { font-size: 14.5px; color: #5a5f72; line-height: 1.7; margin: 0 0 10px; }
        .wr-modal__sub  { font-size: 13px; color: #8890a0; margin: 0 0 28px; }
        .wr-modal__btn {
          display: inline-block; padding: 12px 36px;
          background: linear-gradient(135deg, #013d97, #029add);
          color: #fff; border: none; border-radius: 8px;
          font-size: 15px; font-weight: 800; cursor: pointer;
          font-family: 'Roboto', sans-serif; transition: opacity .2s; letter-spacing: .02em;
        }
        .wr-modal__btn:hover { opacity: .9; }

        /* Focus */
        .wr-tab:focus-visible, .wr-panel__close:focus-visible,
        .wr-submit:focus-visible, .wr-modal__btn:focus-visible,
        .wr-already__btn:focus-visible,
        .wr-input:focus-visible, .wr-select:focus-visible, .wr-textarea:focus-visible,
        .wr-photo-trigger:focus-visible {
          outline: 2px solid #029add; outline-offset: 2px;
        }

        /* Reduced motion */
        @media (prefers-reduced-motion: reduce) {
          .wr-panel, .wr-modal, .wr-modal-backdrop,
          .wr-tab, .wr-tab__chevron, .wr-star, .wr-submit {
            transition-duration: 0ms !important; animation-duration: 0ms !important;
          }
        }

        /* Mobile */
        @media (max-width: 480px) {
          .wr-panel { width: 100vw; z-index: 9999; }
          .wr-overlay { z-index: 9998; }
          .wr-modal-backdrop { z-index: 10000; }
          .wr-modal { padding: 32px 20px 28px; }

          /* Panel is full-width on mobile — hide the floating tab when open.
             The header ✕ button handles closing. Tab reappears when closed. */
          .wr-tab--open { display: none !important; }
        }
      `}</style>
    </>
  );
}